import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatShort } from "@/lib/datetime";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Play, RotateCcw, TriangleAlert, Zap } from "lucide-react";

const KIND = { tag: "tag", number: "part number", name: "name", dataplate: "dataplate / labelled value", keyword: "keyword" };

function EngineSwitch({ engine, busy, onFlip }) {
  const one = engine === "one";
  return (
    <div data-testid="se-switch" className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-xs text-slate-500 mb-1">Every search box is answered by</div>
      <div data-testid="se-engine" className="font-display text-xl font-bold mb-3">{one ? "The one engine" : "Today's search"}</div>
      <Button data-testid="se-flip" disabled={busy} onClick={() => onFlip(one ? "today" : "one")}
        variant={one ? "outline" : "default"} className="h-12 rounded-lg px-5">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : one ? <RotateCcw className="w-4 h-4 mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
        {one ? "Switch back to today's search" : "Switch to the one engine"}
      </Button>
      <p className="text-xs text-slate-500 mt-2">One tap, for everyone, logged. Scans are exact either way.</p>
    </div>
  );
}

function CheckResult({ c }) {
  if (!c) return <p data-testid="se-check-none" className="text-sm text-slate-500">No check has run yet.</p>;
  if (c.status === "running") {
    return <p data-testid="se-check-running" className="text-sm text-slate-600"><Loader2 className="inline w-4 h-4 mr-1 animate-spin" />Checking… {c.progress?.done ?? 0} of {c.progress?.total ?? "?"} values</p>;
  }
  if (c.status === "failed") return <p data-testid="se-check-failed" className="text-sm text-[#B01E28]">The last check stopped before it finished. Run it again.</p>;
  const p = c.parts || {}, t = c.trucks || {};
  return (
    <div data-testid="se-check-result" className="space-y-2 text-sm">
      <div className="flex items-center gap-2">
        {c.passed ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <TriangleAlert className="w-5 h-5 text-[#B01E28]" />}
        <b data-testid="se-check-verdict">{c.passed ? "Finds nothing less than today's search" : "The one engine missed something today's search finds"}</b>
      </div>
      <div data-testid="se-check-parts">Parts: {p.values} values searched on {c.live_parts} live parts · missed by the one engine: <b>{p.missed_values}</b></div>
      {p.letters_values != null && (
        <div data-testid="se-check-letters">Letters inside a part number: {p.letters_values} values with a word of letters · rows added at the end: {p.letters_added_rows ?? 0} · rows shown before that moved: <b>{p.letters_moved_values ?? 0}</b></div>
      )}
      <div data-testid="se-check-trucks">Trucks: {t.values} values on {c.live_trucks} live trucks · missed: <b>{t.missed_values}</b> · found by its own stock number, VIN or make + model: {t.found_own} of {t.values}</div>
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(p.by_kind || {}).filter(([, v]) => v.values).map(([k, v]) => (
          <Badge key={k} data-testid={`se-check-top-${k}`} variant="outline" className="rounded-md font-normal text-slate-600">
            by its {KIND[k]}: near the top {v.top} of {v.values}
          </Badge>))}
      </div>
      <div className="text-xs text-slate-400">{c.kind === "after_deploy" ? "Ran by itself after the deploy" : "Run by hand"} · {formatShort(c.finished_at)} · reads only, writes nothing but this result</div>
    </div>
  );
}

export function SearchEnginePanel() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/search-engine").then(({ data }) => setS(data)).catch(() => setS(false)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (s?.check?.status !== "running") return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [s, load]);
  const flip = async (engine) => {
    setBusy(true);
    try { await api.post("/admin/search-engine", { engine }); toast.success(engine === "one" ? "The one engine is answering" : "Back to today's search"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't switch"); }
    finally { setBusy(false); }
  };
  const run = async () => {
    try { await api.post("/admin/search-check/run"); toast.success("Search check started"); setTimeout(load, 800); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start the check"); }
  };
  if (s === null) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  if (s === false) return <p data-testid="se-error" className="text-sm text-slate-500">Couldn't load the search engine settings.</p>;
  return (
    <div data-testid="se-panel" className="grid md:grid-cols-2 gap-4">
      <EngineSwitch engine={s.engine} busy={busy} onFlip={flip} />
      <div data-testid="se-check" className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="text-xs text-slate-500">Search check — every value on every live part and truck, searched both ways</div>
        <CheckResult c={s.check} />
        <Button data-testid="se-check-run" variant="outline" disabled={s.check?.status === "running"} onClick={run} className="h-12 rounded-lg px-5">
          <Play className="w-4 h-4 mr-2" />Run the check
        </Button>
      </div>
    </div>
  );
}
